import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft,
  User,
  Mail,
  Building2,
  Phone,
  Camera,
  Shield,
  Bell,
  Key,
  Save,
  CheckCircle2
} from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_825c5321-5d95-4d75-842e-1e4ffe27dc80/artifacts/nz2oqlak_image.png';

export const ProfilePage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  const [isSaving, setIsSaving] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    role: '',
  });

  useEffect(() => {
    const userData = localStorage.getItem('costgenie_user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      setFormData({
        name: parsedUser.name || '',
        email: parsedUser.email || '',
        company: parsedUser.company || '',
        phone: parsedUser.phone || '',
        role: parsedUser.role || 'Admin',
      });
    } else {
      navigate('/login');
    }
  }, [navigate]);

  const handleSave = async () => {
    setIsSaving(true);
    
    // Mock save
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Update local storage
    const updatedUser = { ...user, ...formData };
    localStorage.setItem('costgenie_user', JSON.stringify(updatedUser));
    setUser(updatedUser);
    
    setIsSaving(false);
    setShowSaved(true);
    setTimeout(() => setShowSaved(false), 3000);
  };

  if (!user) return null;

  const tabs = [
    { id: 'profile', name: 'Profile', icon: User },
    { id: 'security', name: 'Security', icon: Shield },
    { id: 'notifications', name: 'Notifications', icon: Bell },
  ];

  return (
    <div className="min-h-screen bg-[#030810]" data-testid="profile-page">
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-[-20%] right-[-10%] w-[50vw] h-[50vw] rounded-full bg-cyan-500/5 blur-[100px]" />
        <div className="absolute bottom-[-20%] left-[-10%] w-[40vw] h-[40vw] rounded-full bg-purple-500/5 blur-[80px]" />
      </div>

      <div className="relative z-10">
        {/* Header */}
        <header className="border-b border-white/5">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link 
                to="/dashboard" 
                className="p-2 text-white/60 hover:text-white transition-colors rounded-lg hover:bg-white/5"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <Link to="/">
                <img src={LOGO_URL} alt="CostGenie" className="h-8 w-auto" />
              </Link>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="max-w-4xl mx-auto px-6 py-12">
          <div className="mb-8">
            <h1 className="text-2xl font-semibold mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
              Account Settings
            </h1>
            <p className="text-white/40 text-sm">Manage your profile and preferences</p>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-8 p-1 rounded-xl bg-white/5 w-fit">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-cyan-500 text-[#030810]'
                    : 'text-white/60 hover:text-white hover:bg-white/5'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.name}
              </button>
            ))}
          </div>

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-8"
            >
              {/* Avatar Section */}
              <div className="glass-panel p-6">
                <div className="flex items-center gap-6">
                  <div className="relative">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center text-white text-2xl font-semibold">
                      {formData.name?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <button className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-[#030810] border border-white/10 flex items-center justify-center text-white/60 hover:text-white transition-colors">
                      <Camera className="w-4 h-4" />
                    </button>
                  </div>
                  <div>
                    <p className="font-semibold text-lg">{formData.name}</p>
                    <p className="text-white/40 text-sm">{formData.role} • {user.plan} Plan</p>
                  </div>
                </div>
              </div>

              {/* Profile Form */}
              <div className="glass-panel p-6">
                <h2 className="font-semibold mb-6">Personal Information</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-white/60 text-sm mb-2">Full Name</label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="auth-input pl-11"
                        data-testid="profile-name"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-white/60 text-sm mb-2">Email</label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="auth-input pl-11"
                        data-testid="profile-email"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-white/60 text-sm mb-2">Company</label>
                    <div className="relative">
                      <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                      <input
                        type="text"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        className="auth-input pl-11"
                        data-testid="profile-company"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-white/60 text-sm mb-2">Phone</label>
                    <div className="relative">
                      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="auth-input pl-11"
                        placeholder="+1 (555) 000-0000"
                        data-testid="profile-phone"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex items-center justify-between">
                  <div>
                    {showSaved && (
                      <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="flex items-center gap-2 text-green-400 text-sm"
                      >
                        <CheckCircle2 className="w-4 h-4" />
                        Changes saved
                      </motion.div>
                    )}
                  </div>
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="btn-premium btn-premium-primary"
                    data-testid="save-profile"
                  >
                    {isSaving ? (
                      <span className="relative z-10">Saving...</span>
                    ) : (
                      <>
                        <Save className="w-4 h-4 relative z-10" />
                        <span className="relative z-10">Save Changes</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="glass-panel p-6">
                <h2 className="font-semibold mb-6">Change Password</h2>
                <div className="space-y-4 max-w-md">
                  <div>
                    <label className="block text-white/60 text-sm mb-2">Current Password</label>
                    <div className="relative">
                      <Key className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                      <input
                        type="password"
                        className="auth-input pl-11"
                        placeholder="Enter current password"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-white/60 text-sm mb-2">New Password</label>
                    <div className="relative">
                      <Key className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                      <input
                        type="password"
                        className="auth-input pl-11"
                        placeholder="Enter new password"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-white/60 text-sm mb-2">Confirm New Password</label>
                    <div className="relative">
                      <Key className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                      <input
                        type="password"
                        className="auth-input pl-11"
                        placeholder="Confirm new password"
                      />
                    </div>
                  </div>
                  <button className="btn-premium btn-premium-primary mt-4">
                    <span className="relative z-10">Update Password</span>
                  </button>
                </div>
              </div>

              <div className="glass-panel p-6">
                <h2 className="font-semibold mb-4">Two-Factor Authentication</h2>
                <p className="text-white/40 text-sm mb-4">
                  Add an extra layer of security to your account
                </p>
                <button className="btn-premium btn-premium-outline">
                  <Shield className="w-4 h-4" />
                  <span>Enable 2FA</span>
                </button>
              </div>
            </motion.div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-panel p-6"
            >
              <h2 className="font-semibold mb-6">Notification Preferences</h2>
              <div className="space-y-4">
                {[
                  { label: 'RFQ Updates', description: 'Get notified when RFQ status changes' },
                  { label: 'Quote Approvals', description: 'Receive alerts for pending approvals' },
                  { label: 'Deadline Reminders', description: 'Get reminders before due dates' },
                  { label: 'Team Activity', description: 'Updates on team member actions' },
                  { label: 'Weekly Reports', description: 'Receive weekly summary emails' },
                ].map((item) => (
                  <div key={item.label} className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
                    <div>
                      <p className="text-white/90 text-sm font-medium">{item.label}</p>
                      <p className="text-white/40 text-xs">{item.description}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" defaultChecked className="sr-only peer" />
                      <div className="w-11 h-6 bg-white/10 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-500"></div>
                    </label>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </main>
      </div>
    </div>
  );
};

export default ProfilePage;
